import React from "react";
import {Box, Divider, Drawer, Grid, List} from "@mui/material";
import {useRecoilState, useSetRecoilState} from "recoil";
import {AppointmentBottomDrawerListItem} from "./AppointmentBottomDrawerListItem";
import '../../styles/BottomDrawerStyle.scss';
import {alfyToast, getShortenDateWithDayOfWeek, isSameDate} from "../../utils/Utils";
import {AlfyButton} from "../AlfyButton";
import {AlfyBorderButton} from "../AlfyBorderButton";
import {useParams, useSearchParams} from "react-router-dom";
import {PollItem} from "../../models/PollItem";
import {openAskAttendanceBottomDrawerState} from "../../states/appointment/overviewAtom";
import {useAlfyApi} from "../../hooks/alfyHooks";
import {useFetchAppointmentWithPoll} from "../../states/appointment/server";
import {useFetchMemberMe} from "../../states/member/server";

const AskAttendanceAnswerButtons: React.FC<{ pollItem: PollItem }> = ({ pollItem }) => {
    const setOpenAskAttendanceBottomDrawer = useSetRecoilState(openAskAttendanceBottomDrawerState)
    const { useAnswerAskAttendance } = useAlfyApi()
    const { mutateAsync: answerAttendance } = useAnswerAskAttendance(pollItem.appointmentId)
    const handleAccept = async () => {
        try {
            await answerAttendance(
                {
                    appointmentId: pollItem.appointmentId,
                    attendType: "ATTEND",
                    pollItemId: pollItem.id,
                }
            )
            alfyToast(`${getShortenDateWithDayOfWeek(pollItem.date)}에 대해 '참석가능'으로 투표했습니다.`)
            setOpenAskAttendanceBottomDrawer(false)
        } catch (e) {
            console.error(e)
            alfyToast(`투표에 실패했습니다. 다시 시도해주세요.`)
        }
    }

    const handleReject = async () => {
        try {
            await answerAttendance(
                {
                    appointmentId: pollItem.appointmentId,
                    attendType: "ABSENT",
                    pollItemId: pollItem.id,
                }
            )
            setOpenAskAttendanceBottomDrawer(false)
            alfyToast(`${getShortenDateWithDayOfWeek(pollItem.date)}에 대해 '참석불가'로 투표했습니다.`)
        } catch (e) {
            console.error(e)
            alfyToast(`투표에 실패했습니다. 다시 시도해주세요.`)
        }
    }

    return (
        <Grid item container spacing={1} padding={'0px 16px 16px 16px'}>
            <Grid item xs><AlfyButton text={'네 가능해요'} onClick={handleAccept} /></Grid>
            <Grid item xs><AlfyBorderButton text={'이 날은 안돼요'} onClick={handleReject} /></Grid>
        </Grid>
    )
}

export const AskAttendanceBottomDrawer: React.FC = () => {
    const [openBottomDrawer, setOpenBottomDrawer] = useRecoilState(openAskAttendanceBottomDrawerState);
    const { id: appointmentId } = useParams()
    const { data: appointment } = useFetchAppointmentWithPoll(appointmentId!)
    const { data: memberMe } = useFetchMemberMe(appointmentId!)

    const [searchParams] = useSearchParams()
    const askAttendanceId = searchParams.get('askAttendanceId')! // 밖에서 null체크를 한다
    const targetPollItem = appointment?.pollItems.find(pollItem => pollItem.id === askAttendanceId)!
    const bottomDrawerPollInfo = appointment?.toDatePollInfos().find(datePollInfo => isSameDate(datePollInfo.date, targetPollItem.date))

    const handleClose = (reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") {
            alfyToast(`${getShortenDateWithDayOfWeek(targetPollItem.date)}에 대해 '투표 안함'으로 남겨두었습니다.`)
        }
        setOpenBottomDrawer(false)
    }

    if (!appointment) {
        return <></>
    }

    return (
        <Drawer open={openBottomDrawer}
                onClose={(event, reason) => handleClose(reason)}
                anchor={'bottom'}
                PaperProps={{ style: {maxWidth: '430px', borderTopLeftRadius: 30, borderTopRightRadius: 30} }}
        >
            <Grid container direction={'column'}>
                <Grid item>
                    <Box padding={'16px 16px 0px 16px'}>
                        <span>{memberMe?.name}님, {getShortenDateWithDayOfWeek(targetPollItem.date)}에 시간 괜찮으세요?</span>
                        <p className={'description-text'}>혹시 이 날짜로 약속 잡아도 될까요?</p>
                    </Box>
                    <Divider />
                </Grid>
                <Grid item>
                    <Box sx={{height: 350}} role="presentation" padding={'27px 16px 0px 16px'}>
                        {getBottomDrawerContent()}
                    </Box>
                </Grid>
                <AskAttendanceAnswerButtons pollItem={targetPollItem}/>
            </Grid>
        </Drawer>
    )

    function getBottomDrawerContent(): React.JSX.Element {
        let listItems = bottomDrawerPollInfo?.pollData.map((data) => {
            return <AppointmentBottomDrawerListItem
                key={data.member.id}
                member={data.member}
                attendType={data.attendType}
                withAskAttendanceButton={false}
            />
        })

        if (listItems == null || listItems.length === 0) {
            return (
                <Box paddingBottom={'24px'} height={'100%'}>
                    <span>{getShortenDateWithDayOfWeek(bottomDrawerPollInfo?.date ?? null)}</span>
                    <Grid container justifyContent={'center'} alignItems={'center'} height={'100%'} paddingBottom={'24px'}>
                        <Grid item>
                            <span>아직 이 날짜에 아무도 투표하지 않았어요.</span>
                        </Grid>
                    </Grid>
                </Box>
            )
        }

        return (
            <>
                <span>{getShortenDateWithDayOfWeek(bottomDrawerPollInfo!!.date)}</span>
                <List>
                    {listItems}
                </List>
            </>
        );
    }
}